<template>
    <div class="mcp-code-example">
        <div class="code-tabs">
            <div v-for="lang in languages" :key="lang.key" :class="{ 'code-tab': true, active: activeLang === lang.key }" @click="activeLang = lang.key">
                {{ lang.label }}
            </div>
            <button class="code-copy" @click="copyCode" :title="$t('dash.settings.mcp_docs.reference.copy_code')">
                <i :class="copied ? 'fa-solid fa-check' : 'fa-solid fa-copy'"></i>
            </button>
        </div>
        <pre class="code-panel"><code :class="'language-' + prismLang" v-html="highlighted"></code></pre>
    </div>
</template>

<script>
import Prism from 'prismjs'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-python'

const { VUE_APP_API_HOST } = process.env

/**
 * Renders the SAME JSON-RPC `tools/call` request (identical across every language - only the
 * transport wrapper differs) as curl / JavaScript / Python, for one MCP tool + example arguments.
 * See mcp-documentation-data.js for `exampleArgs` per tool - one object, reused across all 3.
 */
export default {
    name: 'mcp-code-example',

    props: {
        toolName: { type: String, required: true },
        exampleArgs: { type: Object, default: () => ({}) }
    },

    data() {
        return {
            activeLang: 'curl',
            copied: false,
            languages: [
                { key: 'curl', label: 'curl' },
                { key: 'javascript', label: 'JavaScript' },
                { key: 'python', label: 'Python' }
            ]
        }
    },

    computed: {
        mcpEndpoint() {
            return `${VUE_APP_API_HOST}/mcp`
        },

        requestBody() {
            return {
                jsonrpc: '2.0',
                id: 1,
                method: 'tools/call',
                params: { name: this.toolName, arguments: this.exampleArgs }
            }
        },

        bodyJson() {
            return JSON.stringify(this.requestBody, null, 2)
        },

        prismLang() {
            return this.activeLang === 'curl' ? 'bash' : this.activeLang
        },

        code() {
            if (this.activeLang === 'curl') return this.curlCode
            if (this.activeLang === 'javascript') return this.jsCode
            return this.pythonCode
        },

        curlCode() {
            // Single-quoted heredoc-free form (no shell escaping surprises across OSes) - the
            // JSON body is indented to match, matching how requests.http already shows it.
            const indentedBody = this.bodyJson.split('\n').join('\n  ')
            return [
                `curl -X POST "${this.mcpEndpoint}" \\`,
                `  -H "Authorization: Bearer $MASHUP_API_KEY" \\`,
                `  -H "Content-Type: application/json" \\`,
                `  -d '${indentedBody}'`
            ].join('\n')
        },

        jsCode() {
            return [
                `const response = await fetch('${this.mcpEndpoint}', {`,
                `  method: 'POST',`,
                `  headers: {`,
                `    'Authorization': 'Bearer ' + process.env.MASHUP_API_KEY,`,
                `    'Content-Type': 'application/json'`,
                `  },`,
                `  body: JSON.stringify(${this.bodyJson.split('\n').join('\n  ')})`,
                `})`,
                ``,
                `const result = await response.json()`,
                `console.log(result)`
            ].join('\n')
        },

        pythonCode() {
            const pyBody = this.bodyJson
                .replace(/: true/g, ': True')
                .replace(/: false/g, ': False')
                .replace(/: null/g, ': None')
                .split('\n').join('\n    ')
            return [
                `import os, requests`,
                ``,
                `response = requests.post(`,
                `    "${this.mcpEndpoint}",`,
                `    headers={`,
                `        "Authorization": "Bearer " + os.environ["MASHUP_API_KEY"],`,
                `        "Content-Type": "application/json"`,
                `    },`,
                `    json=${pyBody}`,
                `)`,
                ``,
                `print(response.json())`
            ].join('\n')
        },

        highlighted() {
            const grammar = Prism.languages[this.prismLang]
            return grammar ? Prism.highlight(this.code, grammar, this.prismLang) : this.code
        }
    },

    methods: {
        copyCode() {
            const el = document.createElement('textarea')
            el.value = this.code
            el.setAttribute('readonly', '')
            el.style.position = 'absolute'
            el.style.left = '-9999px'
            document.body.appendChild(el)
            const selected = document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false
            el.select()
            document.execCommand('copy')
            document.body.removeChild(el)
            if (selected) {
                document.getSelection().removeAllRanges()
                document.getSelection().addRange(selected)
            }
            this.copied = true
            setTimeout(() => { this.copied = false }, 2000)
        }
    }
}
</script>

<style lang="scss" scoped>
.mcp-code-example {
    background: #1e1b26;
    border-radius: 10px;
    overflow: hidden;

    .code-tabs {
        display: flex;
        align-items: center;
        background: #171420;
        padding: 0 6px;

        .code-tab {
            padding: 10px 14px;
            font-size: 12.5px;
            color: #8b8798;
            cursor: pointer;
            font-weight: 500;

            &.active {
                color: #fff;
                border-bottom: 2px solid #B64985;
            }
        }

        .code-copy {
            margin-left: auto;
            background: transparent;
            border: none;
            color: #8b8798;
            cursor: pointer;
            padding: 8px 12px;
            font-size: 13px;

            &:hover { color: #fff; }
        }
    }

    .code-panel {
        margin: 0;
        padding: 16px;
        overflow-x: auto;
        max-height: 480px;

        code {
            font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
            font-size: 12.5px;
            line-height: 1.6;
            color: #e4e1ec;
            white-space: pre;
        }
    }
}
</style>
